import type { ApiData } from '@app/api/typed';
import { hasKey, t } from '@app/i18n';

export type InfraConfig = ApiData<'/api/v1/infra/config'>;
export type InfraGroup = InfraConfig['groups'][number];
export type InfraField = InfraGroup['fields'][number];
export type InfraApplyResult = ApiData<'/api/v1/infra/config/{group}', 'put'>;
export type InfraTestResult = ApiData<'/api/v1/infra/test/{group}', 'post'>;
export type InfraCheck = InfraTestResult['checks'][number];

/** Nome do grupo aceito por `testInfraConfigGroup` (o contrato não testa `aplicacao`). */
export type GroupTestable = 'apim' | 'azure' | 'cluster' | 'registry' | 'pdp';

/**
 * Valor de um campo no formulário. O tipo vem do middleware (FR-018), não de uma
 * lista nossa: `texto`/`opcao` → string, `lista` → string[], `booleano` → boolean.
 */
export type ValueField = string | string[] | boolean;
export type ValuesGroup = Record<string, ValueField>;

/** Separador da entrada de `lista` — o mesmo que o protótipo usa. */
export const SEPARADOR_LIST = ',';

/**
 * Lê como lista o que o executor publicou num campo `tipo: "lista"`.
 *
 * **Por que isto existe** (lab, 28/08): o middleware real publica lista como
 * **texto** — `GET /config` devolve o que está escrito no EnvironmentFile, então
 * `AIG_MCP_OVERRIDE` chega como a string `["mcp"]`, não como array. O mock
 * publicava array, e a tela quebrava só contra o real, ao desenhar:
 * `(e ?? []).join is not a function`.
 *
 * Quem conforma para valer é a borda do backend
 * (`infrastructure/aig/config_forma.go`); esta função é a segunda barreira, e ela
 * fica porque o custo do erro é a tela inteira não montar. O `oneOf` do contrato
 * (`MiddlewareConfigField.valor`) permite string, lista, bool e nulo — quem lê
 * como lista tem de aguentar as quatro.
 *
 * Formas aceitas, da mais específica para a mais frouxa: array → itens como
 * texto; string com JSON de array (a do real) → itens; texto separado por vírgula
 * (a que esta tela edita) → itens; nulo/vazio → `[]`. Nada é inventado.
 */
export function asList(valor: unknown): string[] {
  if (valor === null || valor === undefined) return [];
  if (Array.isArray(valor))
    return valor.map((item) => (typeof item === 'string' ? item : String(item)));
  if (typeof valor !== 'string') return [];

  const clean = valor.trim();
  if (!clean) return [];

  if (clean.startsWith('[')) {
    try {
      const wasRead: unknown = JSON.parse(clean);
      if (Array.isArray(wasRead)) {
        return wasRead.map((item) => (typeof item === 'string' ? item : String(item)));
      }
    } catch {
      // JSON quebrado cai na vírgula de propósito: o usuário vê o texto cru no
      // campo e pode consertá-lo. Engolir para `[]` esconderia o valor que está
      // lá — e é a edição dele que grava por cima.
    }
  }
  return textForList(clean);
}

/**
 * Lê como booleano o que o executor publicou num campo `tipo: "booleano"`.
 *
 * O real publica o `str(bool)` do Python (`"False"`), pelo mesmo motivo da lista.
 * Sem isto, `valor === true` dava `false` tanto para `"False"` quanto para
 * `"True"` — a caixa aparecia desmarcada com o middleware ligado.
 *
 * O vocabulário é o do `strconv.ParseBool` que a borda do backend usa; o que não
 * estiver nele vira `false`, que é o estado neutro da caixa.
 */
export function asBoolean(valor: unknown): boolean {
  if (typeof valor === 'boolean') return valor;
  if (typeof valor !== 'string') return false;
  return ['true', 't', '1'].includes(valor.trim().toLowerCase());
}

export function listForText(valor: unknown): string {
  return asList(valor).join(`${SEPARADOR_LIST} `);
}

/**
 * Texto do `default` publicado, para servir de placeholder.
 *
 * O contrato deixa `default` ser string **ou** lista (`AIG_APIM_API_VERSION` é
 * string, `AIG_MCP_OVERRIDE` é lista) — tratar tudo como lista quebra a tela.
 */
export function defaultText(valor: unknown): string | undefined {
  if (Array.isArray(valor)) return listForText(valor as string[]) || undefined;
  if (typeof valor === 'string') return valor || undefined;
  if (typeof valor === 'boolean' || typeof valor === 'number') return String(valor);
  return undefined;
}

export function textForList(texto: string): string[] {
  return texto
    .split(SEPARADOR_LIST)
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Conforma a resposta de `GET /infra/config` à forma que o `tipo` promete, antes
 * de ela entrar no cache do react-query (FR-018).
 *
 * É a borda da API da tela: depois daqui, `habilita`, `faltando` e o `valor` de
 * campo de lista **são** listas, e o de campo booleano **é** booleano — nenhum
 * componente precisa se defender. Foi a falta disso que derrubou T20 no lab em
 * 28/08, com `(e ?? []).join is not a function` vindo de um `AIG_MCP_OVERRIDE`
 * que chegou como a string `["mcp"]`.
 *
 * Campo sensível não passa pela conversão: `valor: null` ali é "não se pode ver"
 * (AC-11/FR-020), não "lista vazia", e a tela desenha os dois casos diferente.
 */
export function normalizeInfraConfig(config: InfraConfig): InfraConfig {
  return {
    ...config,
    groups: (config.groups ?? []).map((grupo) => ({
      ...grupo,
      enables: asList(grupo.enables),
      missing: asList(grupo.missing),
      fields: (grupo.fields ?? []).map(normalizeField),
    })),
  };
}

function normalizeField(campo: InfraField): InfraField {
  if (campo.kind === 'lista') {
    return {
      ...campo,
      ...(campo.sensitive ? {} : { value: asList(campo.value) }),
      default: asList(campo.default),
    };
  }
  if (campo.kind === 'booleano') {
    return {
      ...campo,
      ...(campo.sensitive ? {} : { value: asBoolean(campo.value) }),
      default: asBoolean(campo.default),
    };
  }
  return campo;
}

/**
 * Valor inicial de um campo no formulário.
 *
 * Campo sensível **nunca** tem valor (`valor` é sempre `null` no contrato): o
 * formulário começa vazio e só envia se alguém digitar (FR-020).
 */
export function initialValue(campo: InfraField): ValueField {
  if (campo.sensitive) return '';
  if (campo.kind === 'booleano') return asBoolean(campo.value);
  if (campo.kind === 'lista') return listForText(campo.value);
  return typeof campo.value === 'string' ? campo.value : '';
}

export function valuesIniciais(grupo: InfraGroup): ValuesGroup {
  return Object.fromEntries(grupo.fields.map((campo) => [campo.field, initialValue(campo)]));
}

function sameValue(campo: InfraField, current: ValueField, inicial: ValueField): boolean {
  if (campo.kind === 'lista') {
    const a = textForList(String(current));
    const b = textForList(String(inicial));
    return a.length === b.length && a.every((item, i) => item === b[i]);
  }
  return current === inicial;
}

/**
 * Só o que mudou (FR-021). Três regras que o contrato impõe:
 *
 * - campo não editável (`bind_host`, `bind_port`) nunca entra;
 * - campo sensível entra **apenas** quando digitado — em branco significa manter;
 * - `lista` é comparada item a item, para "a, b" e "a,b" não virarem alteração.
 */
export function fieldsAlterados(
  grupo: InfraGroup,
  valores: ValuesGroup,
): Record<string, ValueField> {
  const alterados: Record<string, ValueField> = {};

  for (const campo of grupo.fields) {
    if (!campo.editable) continue;
    const current = valores[campo.field];
    if (current === undefined) continue;

    if (campo.sensitive) {
      const digitado = String(current).trim();
      if (digitado) alterados[campo.field] = digitado;
      continue;
    }

    if (sameValue(campo, current, initialValue(campo))) continue;
    alterados[campo.field] = campo.kind === 'lista' ? textForList(String(current)) : current;
  }

  return alterados;
}

/** `pronto` / `faltam {n}` do badge da aba (FR-019). */
export function groupSummary(grupo: InfraGroup): { pronto: boolean; faltam: number } {
  return { pronto: grupo.ready, faltam: grupo.missing?.length ?? 0 };
}

/**
 * O que a tela mostra: as abas e os campos SEM a marca `hidden` do backend (aim-o3r.14,
 * classe D, decisão de 04/10 — os dois do Kyverno e a aba `agentes` com `debug_agentes`
 * desligado).
 *
 * A regra NÃO mora aqui: quem decide o que esconder é o backend (`usecases/config/labels.go`),
 * e a tela só obedece à marca, como obedece a `testable`. Este módulo continua sem lista
 * própria de campos — e a API continua com o espelho completo do executor, porque o filtro
 * é desta tela, não da resposta.
 */
export function visibleGroups(config: InfraConfig | undefined): InfraGroup[] {
  return (config?.groups ?? []).filter((grupo) => !grupo.hidden);
}

export function visibleFields(grupo: InfraGroup): InfraField[] {
  return grupo.fields.filter((campo) => !campo.hidden);
}

export function groupByName(config: InfraConfig | undefined, nome: string): InfraGroup | undefined {
  return config?.groups.find((grupo) => grupo.name === nome);
}

// ── rótulos pela `key` do contrato (marco 3, qa-i18n) ──────────────────────────
//
// A tela é GERADA pelo middleware (FR-018): rótulo, dica e descrição chegavam prontos em
// pt-BR e ficavam em português no console em inglês. Agora cada grupo, campo e check
// traz uma `key` EN estável; o texto sai do catálogo `infrastructure.config.<key>.*` e,
// se o catálogo não a tiver (campo que o middleware publicou ontem), cai no texto do
// servidor — a tela nunca mostra chave crua nem quebra por campo novo.

function fromCatalog(key: string | undefined, part: string): string | undefined {
  if (!key) return undefined;
  const k = `infrastructure.config.${key}.${part}`;
  return hasKey(k) ? t(k) : undefined;
}

export function groupLabel(group: InfraGroup): string {
  return fromCatalog(group.key, 'label') ?? group.label ?? group.name;
}

export function groupDescription(group: InfraGroup): string | undefined {
  return fromCatalog(group.key, 'description') ?? group.description ?? undefined;
}

export function groupWarning(group: InfraGroup): string | null {
  return fromCatalog(group.key, 'warning') ?? group.warning ?? null;
}

export function fieldLabel(field: InfraField): string {
  return fromCatalog(field.key, 'label') ?? field.label ?? field.name;
}

export function fieldHelp(field: InfraField): string | undefined {
  return fromCatalog(field.key, 'help') ?? field.help ?? undefined;
}

/**
 * Rótulo de uma opção de um campo `opcao` (aim-uig.40 §5.3), pelo catálogo
 * `infrastructure.config.<key>.option.<valor>`. Sem a chave, `null` — e a tela
 * mostra o valor cru, em `mono`, como sempre mostrou: o middleware pode publicar
 * uma opção que o catálogo ainda não conhece, e a tela não pode quebrar por isso.
 *
 * O valor da opção é DADO do contrato (`block`, `allow`), e por isso fica byte a
 * byte no fim da chave; só o texto que a pessoa lê vai ao catálogo.
 */
export function optionLabel(field: InfraField, valor: string): string | null {
  return fromCatalog(field.key, `option.${valor}`) ?? null;
}

export function checkLabel(check: InfraCheck): string {
  return fromCatalog(check.key, 'label') ?? check.label ?? check.name;
}
